"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { RiSendPlaneFill } from "react-icons/ri";
import { usePathname } from "next/navigation";
import { ComposerAttachButton, ComposerFilePreviews, useComposerFiles } from "@/components/team-chat/composer-files";
import TiptapEditor from "@/components/tiptap/editor";
import { useWebSocket } from "@/contexts/websocket-provider";
import { useNotificationSound } from "@/hooks/use-notification-sound";
import { useProviders } from "@/hooks/use-providers";
import createId from "@/lib/create-id";
import { formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { SuggestionItem } from "@/types/suggestions";
import { patientQueryKeys, patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { useSendTeamMessage } from "@/query/team-chat";
interface MessageInputProps {
  conversationId: string;
  onMessageSent?: () => void;
  disabled?: boolean;
}
export function MessageInput({
  conversationId,
  onMessageSent,
  disabled = false
}: MessageInputProps) {
  const [content, setContent] = useState("");
  const {
    selectedFiles,
    setSelectedFiles,
    addFiles,
    removeFile,
    isDragOver,
    dropZoneProps
  } = useComposerFiles();
  const {
    playSendSound
  } = useNotificationSound();
  // Mentionable = can receive a Whisper, same as the conversation pickers.
  const {
    providers
  } = useProviders({
    hasFirebaseUid: true
  });
  const sendMessage = useSendTeamMessage(conversationId);
  const {
    sendMessage: wsSendMessage
  } = useWebSocket();
  const lastTypingSentRef = useRef(0);
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const contextPatientId = useMemo(() => {
    const match = pathname.match(/\/patient\/(pat_[a-zA-Z0-9]+)/);
    return match?.[1] ?? null;
  }, [pathname]);
  const providerSuggestions = useMemo<SuggestionItem[]>(() => providers.map(provider => ({
    id: provider.id,
    label: formatStaffName(provider),
    content: provider.roles.map(role => role.name).join(", ") || "Provider",
    type: "TEAM" as const
  })), [providers]);
  const handleSend = useCallback(() => {
    const trimmed = content.trim();
    if (!trimmed && selectedFiles.length === 0 || sendMessage.isPending) return;
    setContent("");
    setSelectedFiles([]);
    playSendSound();
    sendMessage.mutate({
      content: trimmed,
      files: selectedFiles.length > 0 ? selectedFiles : undefined,
      tempId: `pending_${createId("tmp")}`,
      onOptimistic: onMessageSent
    }, {});
  }, [content, selectedFiles, sendMessage, playSendSound, onMessageSent]);
  const handleEditorUpdate = useCallback((text: string) => {
    setContent(text);
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      wsSendMessage(JSON.stringify({
        type: "team_typing",
        conversationId
      }));
    }
  }, [wsSendMessage, conversationId]);
  const handleSearchSuggestions = useCallback(async (query: string): Promise<SuggestionItem[]> => {
    const lowercaseQuery = query.toLowerCase();
    const matchingProviders = providerSuggestions.filter(item => item.label.toLowerCase().includes(lowercaseQuery) || item.content.toLowerCase().includes(lowercaseQuery));
    if (!query || query.length < 2) {
      if (!contextPatientId) return matchingProviders;
      try {
        const patient = await queryClient.fetchQuery({
          ...patientQueryOptions.detail(contextPatientId),
          staleTime: 30_000
        });
        if (!patient) return matchingProviders;
        return [...matchingProviders, {
          id: patient.id,
          label: `${patient.firstName} ${patient.lastName ?? ""}`.trim(),
          content: patient.id,
          type: "PATIENT" as const
        }];
      } catch {
        return matchingProviders;
      }
    }
    await queryClient.cancelQueries({
      queryKey: patientQueryKeys.searches()
    });
    try {
      const patients = await queryClient.fetchQuery({
        ...patientQueryOptions.search(query),
        staleTime: 30_000
      });
      const patientSuggestions = (patients ?? []).map(patient_0 => ({
        id: patient_0.id,
        label: `${patient_0.firstName} ${patient_0.lastName ?? ""}`.trim(),
        content: patient_0.id,
        type: "PATIENT" as const
      }));
      return [...matchingProviders, ...patientSuggestions];
    } catch {
      return matchingProviders;
    }
  }, [contextPatientId, providerSuggestions, queryClient]);
  const canSend = !disabled && (content.trim() || selectedFiles.length > 0);
  return <div className={cx("border-t border-border px-3 py-2", isDragOver && "bg-primary/5")} {...dropZoneProps}>
      <ComposerFilePreviews files={selectedFiles} onRemove={removeFile} />

      {/* Input container */}
      <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-md transition-colors duration-300 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20">
        <div className="flex items-center gap-2 px-2 py-2">
          <ComposerAttachButton onFiles={addFiles} />

          {disabled ? <textarea value="" placeholder="Starting conversation…" rows={1} disabled className="max-h-40 flex-1 resize-none border-0 bg-transparent py-1 text-body leading-5 text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60" /> : <TiptapEditor content={content} editorClassName="max-h-40 flex-1 border-0 bg-transparent px-0 py-1 text-body leading-5 text-foreground shadow-none hover:border-transparent focus:border-transparent [&_p]:m-0" parentClassName="min-w-0 flex-1" placeholder="Type a message..." onUpdate={handleEditorUpdate} onEnter={handleSend} suggestions={providerSuggestions} onSearchSuggestions={handleSearchSuggestions} textArea={false} />}

          <button onClick={handleSend} disabled={!canSend || sendMessage.isPending} aria-label="Send message" className={cx("flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors", canSend ? "bg-primary text-primary-foreground hover:bg-primary/90" : "text-foreground-muted")}>
            <RiSendPlaneFill className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-1 text-caption text-foreground-muted">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>;
}